/**
 * Created by neo on 30.12.22.
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import ReactPlayer from 'react-player';
import { useState, useEffect, useCallback, useRef } from 'react';
import { OnProgressData, VideoPlayerStore } from './VideoPlayerStore';
import { runInAction } from 'mobx';
import styled from '@emotion/styled';

const VideoContainer = styled.div`
  video {
    object-fit: cover;
  }
`;

const VideoPlayer = styled(ReactPlayer)``;

export type KinasticVideoProps = React.VideoHTMLAttributes<HTMLVideoElement> & {
  isMuted?: boolean;
};

export const KinasticVideo: React.FC<KinasticVideoProps> = observer(
  ({ onDurationChange, onPause, onPlay, onTimeUpdate, isMuted = true, ...moreProps }) => {
    const [videoStore] = useState(() => new VideoPlayerStore());
    const [muted, setMuted] = useState(isMuted);
    const mutedRef = useRef(isMuted);
    const mutedBeforeFullscreenRef = useRef(isMuted);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      mutedRef.current = muted;
    }, [muted]);

    useEffect(() => {
      const handleFullscreenChange = () => {
        const isFullscreen = !!document.fullscreenElement;
        if (isFullscreen) {
          mutedBeforeFullscreenRef.current = mutedRef.current;
          setMuted(false);
        } else {
          setMuted(mutedBeforeFullscreenRef.current);
        }
      };

      document.addEventListener('fullscreenchange', handleFullscreenChange);
      document.addEventListener('webkitfullscreenchange', handleFullscreenChange);

      return () => {
        document.removeEventListener('fullscreenchange', handleFullscreenChange);
        document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      };
    }, []);

    useEffect(() => {
      setMuted(isMuted);
    }, [isMuted]);

    const handleDuration = useCallback((e) => videoStore.onDuration(e.currentTarget.duration), [videoStore]);

    const handlePause = useCallback(
      (e) => {
        runInAction(() => (videoStore.paused = true));
        onPause && onPause(e);
      },
      [onPause, videoStore],
    );

    const handlePlay = useCallback(
      (e) => {
        runInAction(() => (videoStore.paused = false));
        onPlay && onPlay(e);
      },
      [onPlay, videoStore],
    );

    const handleProgress = useCallback(
      (data) => {
        videoStore.handleProgress(data);
        onTimeUpdate && onTimeUpdate(data);
      },
      [onTimeUpdate, videoStore],
    );

    return (
      <VideoContainer ref={containerRef}>
        <VideoPlayer
          playsInline={true}
          width="100%"
          height="100%"
          muted={muted}
          controls={true}
          playing={true}
          onPlay={handlePlay}
          onPause={handlePause}
          onDurationChange={handleDuration}
          onTimeUpdate={handleProgress}
          style={{ objectFit: 'cover' }}
          {...moreProps}
        />
      </VideoContainer>
    );
  },
);
